// 라군 규칙과 NPC: 헤엄(물이 깊을수록 빠르게), 바 도착·주문, 헹굼 폭포, NPC 판단, 29마리 배치.
// 프로토타입 바 2529–2571, lagoonStep 2572–2594, lagoonThink 2595–2616, goToBar·startRinse·goToRinse 2617–2634, 배치 2509–2520, lagoonTap 2650–2658.
import * as THREE from 'three';
import { MENUS, type DishId, type HeadItemId, type LagoonMaskId } from '@capy/shared';
import { rand, rnd } from '../../engine/math';
import { makeAgent, setSize, type Capy, type EmoteKey } from '../../capy/agent';
import { moveTo } from '../../capy/walker';
import { climbStep, hopDown, startClimb } from '../../capy/stacking';
import { canClimb } from '../../capy/stackRules';
import { setFace, setHead, type FaceKey } from '../../capy/equip';
import { giveCarry } from '../../food/eating';
import type { FurTone } from '../../capy/model';
import { MASKBAR, SLUSH, lagoonRandom, lagoonWet, type Water } from './layout';
import type { Rinse } from './scene';

export type BarJob = 'mask' | 'slush';

export interface LagoonCapy extends Capy {
  mode: 'idle' | 'wander' | 'stroll' | 'goto' | 'climb' | 'riding' | 'hopping';
  wander: THREE.Vector3 | null;
  /** 도착하면 할 일: 바 주문 또는 헹굼. */
  onArrive: BarJob | 'rinse' | null;
  next: number;
  rideUntil?: number;
}

/** NPC가 가끔 하는 말 (8글자 규칙 안). */
export const PHRASES = ['yay', 'yay yay', 'i am', 'baby', 'brrr', 'my my', 'ayy', 'crab', 'i pray', 'aim', 'bam', 'mimi', 'i am a capybara', 'yay baby', 'pray', 'my bay', 'ab ab', 'mmm', 'ayy ayy'];

export interface LagoonRules {
  W: Water;
  agents: LagoonCapy[];
  world: THREE.Object3D;
  rinse: Rinse | null;
  /** 지금 헹굼 폭포를 쓰는 카피바라 (한 번에 한 마리). */
  rinseUser: LagoonCapy | null;
  /** 내 카피바라가 막 바·폭포를 떠났으면 다시 다가갈 때까지 자동으로 열지 않는다. */
  guard: { mask: boolean; slush: boolean; rinse: boolean };
  /** 내 카피바라 앞에 열린 바 메뉴. */
  barOpen: BarJob | null;
  say(a: Capy, text: string): void;
  /** 내 카피바라가 바에 도착했을 때 (메뉴 열기). */
  onBar?(job: BarJob): void;
  /** 내 카피바라가 폭포 아래서 씻기 시작했을 때 (팩이 있었는지). */
  onRinse?(masked: boolean): void;
}

const v3 = (p: { x: number; z: number }) => new THREE.Vector3(p.x, 0, p.z);

/** 바 메뉴 하나를 고른 결과: 팩은 얼굴에 바르고, 슬러시는 컵을 손에 든다. */
export function applyBarItem(R: LagoonRules, a: LagoonCapy, job: BarJob, key: LagoonMaskId | DishId, t: number): void {
  if (job === 'mask') { a.targetFacing = MASKBAR.face; setFace(a, key as LagoonMaskId); a.maskPop = t; return; }
  a.targetFacing = SLUSH.face; giveCarry(a, key as DishId, R.world, t);
}

function doBarJob(R: LagoonRules, a: LagoonCapy, job: BarJob, t: number) {
  if (a.isPlayer) { R.guard[job] = true; a.onArrive = null; a.targetFacing = job === 'mask' ? MASKBAR.face : SLUSH.face; R.barOpen = job; R.onBar?.(job); return; }
  const items = Object.keys(job === 'mask' ? MENUS.maskbar : MENUS.slushbar) as (LagoonMaskId | DishId)[];
  applyBarItem(R, a, job, items[Math.floor(Math.random() * items.length)]!, t);
}

/** 헹굼 폭포 아래 서서 씻기 시작 (팩이 있으면 씻겨 나간다). */
export function startRinse(R: LagoonRules, a: LagoonCapy, t: number): void {
  if (R.rinseUser && R.rinseUser !== a && R.rinseUser.washT) return;
  a.washT = t; R.rinseUser = a; a.targetFacing = R.rinse!.face; a.napping = false; a.emote = null;
  if (a.isPlayer) { R.guard.rinse = true; a.onArrive = null; R.onRinse?.(!!a.mask); }
}

/** 라군에서 한 프레임 이동. 움직였으면 true. 프로토타입 lagoonStep 2572–2594. */
export function lagoonStep(R: LagoonRules, a: LagoonCapy, t: number, dt: number): boolean {
  if (a.riding || a.hop || a.mode === 'riding' || a.mode === 'hopping') { a.bathAmt = 0; return false; }
  if (a.mode === 'climb') {
    return climbStep(a, t, dt, R.W.nav, R.agents, { speedK: 1 + 1.0 * a.bathAmt, wet: (x, z) => lagoonWet(R.W, x, z) });
  }
  let moving = false;
  if ((a.mode === 'goto' || a.mode === 'wander' || a.mode === 'stroll') && a.wander) {
    // 물에서는 헤엄이 걷기보다 빠르다 (깊을수록 최대 2배)
    moving = moveTo(a, a.wander, dt, t, R.W.nav, R.agents, false, undefined, 1 + 1.0 * a.bathAmt);
    if (!moving) {
      const job = a.onArrive; a.onArrive = null;
      if (a.mode === 'stroll') a.wander = v3(lagoonRandom(R.W, a.pos, null));
      else { a.mode = 'idle'; a.wander = null; }
      if (job === 'rinse') { if (R.rinse && Math.hypot(a.pos.x - R.rinse.spot.x, a.pos.z - R.rinse.spot.z) < 1.6) startRinse(R, a, t); }
      else if (job) { const B = job === 'mask' ? MASKBAR : SLUSH; if (Math.hypot(a.pos.x - B.spot.x, a.pos.z - B.spot.z) < 3.2) doBarJob(R, a, job, t); }
    }
  }
  if (a.washT && R.rinse) { const k = Math.min(1, dt * 2.5); a.pos.x += (R.rinse.spot.x - a.pos.x) * k; a.pos.z += (R.rinse.spot.z - a.pos.z) * k; }
  else if (!moving) {
    let sx = 0, sz = 0;
    for (const o of R.agents) {
      if (o === a || o.riding || o.hop) continue;
      const ox = a.pos.x - o.pos.x, oz = a.pos.z - o.pos.z, od = Math.hypot(ox, oz);
      if (od < 1.6 && od > 0.001) { const w = (1.6 - od) / 1.6; sx += ox / od * w; sz += oz / od * w; }
    }
    if (Math.hypot(sx, sz) > 0.05) { const nx = a.pos.x + sx * dt * 0.35, nz = a.pos.z + sz * dt * 0.35; if (!R.W.nav.blockedAt(nx, nz)) { a.pos.x = nx; a.pos.z = nz; } }
  }
  a.bathAmt += (lagoonWet(R.W, a.pos.x, a.pos.z) - a.bathAmt) * Math.min(1, dt * 0.9);
  return moving;
}

const EMOTES: EmoteKey[] = ['wave', 'wobble', 'yawn'];

/** NPC 판단: 말하기, 헤엄치러 가기, 팩 바·슬러시 바, 올라타기, 헹굼, 감정 동작, 낮잠. 프로토타입 lagoonThink 2595–2616. */
export function lagoonThink(R: LagoonRules, a: LagoonCapy, t: number): void {
  if (a.isPlayer) return;
  if (a.mode === 'riding' || a.mode === 'hopping') {
    if (a.mode === 'riding' && a.riding && t > (a.rideUntil || 0)) hopDown(a, t, R.W.nav, (x) => { const q = x as LagoonCapy; q.mode = 'wander'; q.wander = v3(lagoonRandom(R.W, q.pos, null)); });
    a.next = t + 2; return;
  }
  if (t < a.next || a.mode === 'climb' || a.eating) return;
  if (a.mode !== 'idle') { a.next = t + 2; return; }
  const r = Math.random(), jit = () => new THREE.Vector3(rand(-1.6, 1.6), 0, rand(-0.6, 0.6));
  if (r < 0.15) R.say(a, PHRASES[Math.floor(Math.random() * PHRASES.length)]!);
  else if (r < 0.52) { a.mode = 'wander'; a.wander = v3(lagoonRandom(R.W, a.pos, Math.random() < 0.85)); }
  else if (r < 0.6 && !a.mask) { a.mode = 'goto'; a.wander = v3(MASKBAR.spot).add(jit()); a.onArrive = 'mask'; }
  else if (r < 0.68) { a.mode = 'goto'; a.wander = v3(SLUSH.spot).add(jit()); a.onArrive = 'slush'; }
  else if (r < 0.74) {
    const near = R.agents.filter((o) => o !== a && !o.hop && Math.hypot(o.pos.x - a.pos.x, o.pos.z - a.pos.z) < 7 && canClimb(a, o));
    if (near.length && startClimb(a, near[Math.floor(Math.random() * near.length)]!)) a.rideUntil = t + rand(15, 35);
  }
  else if (r < 0.79 && a.mask && R.rinse && !R.rinseUser) { a.mode = 'goto'; a.wander = R.rinse.spot.clone(); a.onArrive = 'rinse'; }
  else if (r < 0.84) { a.emote = EMOTES[Math.floor(Math.random() * 3)]!; a.emoteStart = t; }
  else if (r < 0.9) a.napping = !a.napping;
  a.next = t + rand(4, 11);
}

/** 내 카피바라: 바로 헤엄쳐 가기 (이미 가는 중이면 취소). */
export function goToBar(R: LagoonRules, a: LagoonCapy, which: BarJob, t: number): void {
  if (a.mode === 'goto' && a.onArrive === which) { a.mode = 'idle'; a.wander = null; a.onArrive = null; return; }
  const B = which === 'mask' ? MASKBAR : SLUSH;
  const go = () => { a.napping = false; a.emote = null; a.climbT = null; R.guard[which] = false; a.mode = 'goto'; a.wander = v3(B.spot); a.onArrive = which; };
  if (a.riding || a.hop) hopDown(a, t, R.W.nav, go); else go();
}

export function goToRinse(R: LagoonRules, a: LagoonCapy, t: number): void {
  if (!R.rinse) return;
  if (a.mode === 'goto' && a.onArrive === 'rinse') { a.mode = 'idle'; a.wander = null; a.onArrive = null; return; }
  const spot = R.rinse.spot;
  const go = () => { a.napping = false; a.emote = null; a.climbT = null; R.guard.rinse = false; a.mode = 'goto'; a.wander = spot.clone(); a.onArrive = 'rinse'; };
  if (a.riding || a.hop) hopDown(a, t, R.W.nav, go); else go();
}

/** 매 프레임 (내 카피바라): 폭포 아래 들어서면 씻기, 바 가까이 가면 메뉴 열기, 멀어지면 닫기. 프로토타입 barProximity 2635–2649. */
export function barProximity(R: LagoonRules, a: LagoonCapy, t: number): void {
  if (R.rinse && !a.riding && !a.hop) {
    const d = Math.hypot(a.pos.x - R.rinse.spot.x, a.pos.z - R.rinse.spot.z);
    if (R.guard.rinse) { if (d > 2.6) R.guard.rinse = false; }
    else if (d < 1.0 && !a.washT) { if (a.mode === 'goto') { a.mode = 'idle'; a.wander = null; } startRinse(R, a, t); }
  }
  if (R.barOpen) { const B = R.barOpen === 'mask' ? MASKBAR : SLUSH; if (Math.hypot(a.pos.x - B.spot.x, a.pos.z - B.spot.z) > 4.2 || a.riding) R.barOpen = null; }
  if (a.riding || a.hop || a.mode === 'climb') return;
  ([[MASKBAR, 'mask'], [SLUSH, 'slush']] as const).forEach(([B, job]) => {
    const d = Math.hypot(a.pos.x - B.spot.x, a.pos.z - B.spot.z);
    if (R.guard[job]) { if (d > 4.5) R.guard[job] = false; return; }
    if (d < 2.2) { if (a.mode === 'goto') { a.mode = 'idle'; a.wander = null; } doBarJob(R, a, job, t); }
  });
}

/** 내 카피바라가 바닥(물)을 탭했을 때: 폭포·바 근처면 그리로, 아니면 그 자리로 헤엄쳐 간다. */
export function lagoonTap(R: LagoonRules, a: LagoonCapy, hx: number, hz: number, t: number): void {
  a.napping = false; a.emote = null; a.onArrive = null;
  if (R.rinse && (Math.hypot(hx - R.rinse.rock.x, hz - R.rinse.rock.z) < 2.0 || Math.hypot(hx - R.rinse.spot.x, hz - R.rinse.spot.z) < 1.2)) { goToRinse(R, a, t); return; }
  if (Math.hypot(hx - SLUSH.hut.x, hz - SLUSH.hut.z) < 3.4) { goToBar(R, a, 'slush', t); return; }
  if (Math.hypot(hx - MASKBAR.hut.x, hz - MASKBAR.hut.z) < 3.0) { goToBar(R, a, 'mask', t); return; }
  a.mode = 'goto'; a.wander = new THREE.Vector3(hx, 0, hz);
}

export function asLagoon(a: Capy): LagoonCapy {
  return Object.assign(a, { mode: 'idle', wander: null, onArrive: null, next: 0 } as const) as unknown as LagoonCapy;
}

/** NPC 29마리: 라군 소품을 쓰고, 일부는 팩을 바르고, 21마리는 물에서 시작. 프로토타입 2509–2520. */
export function populate(R: LagoonRules): LagoonCapy[] {
  const tones: FurTone[] = ['classic', 'honey', 'cocoa'];
  const lpool: (HeadItemId | 'none')[] = ['none', 'lopapeysa', 'lopapeysa', 'lopapeysa', 'puffin', 'puffin', 'glacier', 'minigeyser', 'auroracloud', 'hotdog', 'duckling', 'orange', 'frog', 'none'];
  const out: LagoonCapy[] = [];
  for (let i = 0; i < 29; i++) {
    const a = asLagoon(makeAgent(R.world, false, tones[(i + 1) % 3]!));
    setHead(a, lpool[Math.floor(rnd(i + 1500) * lpool.length)]!);
    if (i % 9 === 2) setSize(a, 0.5);
    const fr = rnd(i + 1600), face: FaceKey | null = fr < 0.22 ? 'silica' : fr < 0.3 ? 'algae' : fr < 0.35 ? 'lavaclay' : fr < 0.42 ? 'bubble' : null;
    if (face) setFace(a, face);
    const p = i < 21 ? lagoonRandom(R.W, null, true) : lagoonRandom(R.W, null, false);
    a.pos.set(p.x, 0, p.z); a.c.root.position.set(p.x, 0, p.z); a.facing = a.targetFacing = rand(-Math.PI, Math.PI); a.bathAmt = i < 21 ? 1 : 0; a.next = rand(1, 10);
    R.agents.push(a); out.push(a);
  }
  return out;
}
